import React from "react";
import * as d3 from 'd3';
import { Grid, Paper, Typography, Box } from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import { ResizableTable } from "../../../components/resizableTable/ResizableTable";

const SalesForecastNextQtrPlus1Paper = ({
  classes,
  salesForecastNextQuarterPlus1Data,
  isSalesForecastNextQuarterPlus1Loading,
  forecastResizableColumns,
  forecastGetCellValue,
  handleOppRowClick,
  createSummaryTableColumns,
  currencySymbol,
}) => {
  const forecastOpps = salesForecastNextQuarterPlus1Data?.forecastOpps || [];

  const totalACV = forecastOpps.reduce((sum, row) => {
    const val = row.Acv_At_Time_T ?? row.ACV ?? 0;
    return sum + (typeof val === 'number' ? val : 0);
  }, 0);

  // quotaInMillions is in millions; multiply by 1,000,000 to get raw ACV units
  const quotaRaw =
    salesForecastNextQuarterPlus1Data?.summary?.quotaInMillions != null
      ? parseFloat(salesForecastNextQuarterPlus1Data.summary.quotaInMillions) * 1_000_000
      : null;

  const oppsWithSummary =
    forecastOpps.length > 0
      ? [
          ...forecastOpps,
          {
            isTotal: true,
            Opp_ID: '__total__',
            OppName: `Total (${forecastOpps.length} opps)`,
            Acv_At_Time_T: totalACV,
            ACV: totalACV,
          },
          ...(quotaRaw != null
            ? [
                {
                  isQuota: true,
                  Opp_ID: '__quota__',
                  OppName: 'Quota',
                  Acv_At_Time_T: quotaRaw,
                  ACV: quotaRaw,
                },
              ]
            : []),
        ]
      : forecastOpps;

  const getNextQtrPlus1CellValue = (row, field, rowIndex) => {
    if (row.isTotal || row.isQuota) {
      if (field === 'rowNum') return '-';
      if (field === 'OppName') return row.OppName;
      if (field === 'Acv_At_Time_T' || field === 'ACV') {
        const val = row[field];
        return val != null ? d3.format(',.0f')(val / 1000) : '';
      }
      return '';
    }
    return forecastGetCellValue(row, field, rowIndex);
  };

  const getNextQtrPlus1CellStyle = (row) => {
    if (row.isTotal) return { backgroundColor: '#f5f5f5', fontWeight: 700 };
    if (row.isQuota) return { backgroundColor: '#fff9c4', fontWeight: 700 };
    if (row.Is_New_This_Week === 'Y') return { backgroundColor: '#c8e6c9' };
    return {};
  };

  return (
    <Grid item xs={12}>
      <Paper className={classes.paper}>
        <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
          <Typography variant="h6" className={classes.title}>
            Sales Forecast – Next Quarter +1
            {salesForecastNextQuarterPlus1Data?.targetQuarter
              ? ` (${salesForecastNextQuarterPlus1Data.targetQuarter})`
              : ""}
          </Typography>
          <span className={classes.updateFrequency}>{"Live Data"}</span>
        </Box>

        {isSalesForecastNextQuarterPlus1Loading ? (
          <Skeleton variant="rect" animation="wave" height={200} width="100%" />
        ) : (
          <>
            {/* Summary */}
            <Box mb={2}>
              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                Summary
              </Typography>
              <ResizableTable
                columns={createSummaryTableColumns(currencySymbol)}
                rows={[
                  {
                    label: "Won QTD",
                    count: salesForecastNextQuarterPlus1Data?.summary?.won?.count || 0,
                    acvInMillions: salesForecastNextQuarterPlus1Data?.summary?.won?.acvInMillions || "0.00",
                    rowType: "won",
                  },
                  {
                    label: "Most Likely + Commit",
                    count: salesForecastNextQuarterPlus1Data?.summary?.forecast?.count || 0,
                    acvInMillions: salesForecastNextQuarterPlus1Data?.summary?.forecast?.acvInMillions || "0.00",
                    rowType: "forecast",
                  },
                  {
                    label: "Total Forecast",
                    count: salesForecastNextQuarterPlus1Data?.summary?.total?.count || 0,
                    acvInMillions: salesForecastNextQuarterPlus1Data?.summary?.total?.acvInMillions || "0.00",
                    rowType: "total",
                  },
                  {
                    label: "Forecast Goal (1.5x Quota)",
                    count: "",
                    acvInMillions: salesForecastNextQuarterPlus1Data?.summary?.forecastGoal || "0.00",
                    rowType: "forecast",
                  },
                  {
                    label: "Quota",
                    count: "",
                    acvInMillions: salesForecastNextQuarterPlus1Data?.summary?.quotaInMillions || "0.00",
                    rowType: "quota",
                  },
                ]}
                getCellValue={(row, field) => {
                  if (field === "label") return row[field];
                  return row[field] != null ? row[field] : "";
                }}
                getCellStyle={(row, field) => {
                  const baseStyle = {};
                  if (field === "label") baseStyle.fontWeight = 600;
                  if (row.rowType === "total") {
                    baseStyle.backgroundColor = "#f5f5f5";
                    if (field === "count" || field === "acvInMillions") baseStyle.fontWeight = 600;
                  }
                  if (row.rowType === "quota") {
                    baseStyle.backgroundColor = "#fff9c4";
                    if (field === "acvInMillions") baseStyle.fontWeight = 600;
                  }
                  return baseStyle;
                }}
                rowKey="label"
                hideSubHeaderRow={true}
                maxWidth="34%"
              />
            </Box>

            {/* Forecast Opps Table */}
            <Box mb={3}>
              <Typography
                variant="subtitle1"
                style={{ fontWeight: 600, marginTop: 70 }}
              >
                Sales Forecast – Next Quarter +1
                {salesForecastNextQuarterPlus1Data?.targetQuarter
                  ? ` (${salesForecastNextQuarterPlus1Data.targetQuarter})`
                  : ""}{" "}
                – Opps in Forecast – Most Likely + Commit
              </Typography>
              {forecastOpps.length === 0 ? (
                <Box
                  textAlign="center"
                  py={4}
                  style={{ fontStyle: "italic", color: "#666" }}
                >
                  No forecast opportunities for next quarter +1
                </Box>
              ) : (
                <ResizableTable
                  columns={forecastResizableColumns}
                  rows={oppsWithSummary}
                  getCellValue={getNextQtrPlus1CellValue}
                  getCellStyle={getNextQtrPlus1CellStyle}
                  rowKey="Opp_ID"
                  hideSubHeaderRow={true}
                  maxWidth="100%"
                  onRowClick={(row) => {
                    if (row.isTotal || row.isQuota) return;
                    handleOppRowClick && handleOppRowClick(row);
                  }}
                />
              )}
            </Box>

            {/* Legend for green highlighting */}
            <Box mt={2}>
              <div className={classes.legend}>
                <div className={classes.legendItem}>
                  <div
                    className={classes.legendBox}
                    style={{ backgroundColor: "#c8e6c9" }}
                  />
                  <Typography variant="body2">New this week</Typography>
                </div>
              </div>
            </Box>
          </>
        )}
      </Paper>
    </Grid>
  );
};

export default SalesForecastNextQtrPlus1Paper;
